import React from 'react';
import { Shield, Mail, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Header } from './Header';
import { Footer } from './Footer';
import { usePageMeta } from '../hooks/usePageMeta';

const Section = ({ id, title, children }) => (
  <section id={id} data-testid={`privacy-section-${id}`} className="mb-10">
    <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 leading-tight">
      {title}
    </h2>
    <div className="space-y-4 text-gray-700 text-base leading-relaxed">
      {children}
    </div>
  </section>
);

export const PrivacyPolicy = () => {
  usePageMeta({
    title: 'Privacy Policy | Tower Technology',
    description:
      'Read the Tower Technology Privacy Policy and learn how information submitted through our engineering project inquiry website is collected, used and protected.',
    canonical: 'https://towertech-eng.com/privacy-policy',
  });
  return (
    <>
      <Header />
      <main className="pt-32 pb-24 bg-gradient-to-br from-gray-50 via-blue-50 to-gray-50 min-h-screen">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            {/* Header */}
            <div className="mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-700 rounded-full mb-6">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                Privacy Policy
              </h1>
              <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-blue-800 mb-6"></div>
              <p className="text-base md:text-lg text-gray-600">
                Effective date: September 2, 2026
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-7 md:p-10">
              <p className="text-gray-700 leading-relaxed mb-8">
                Xiamen Tower Technology Co., Ltd. ("Tower Technology", "we",
                "us") operates the website{' '}
                <a
                  href="https://towertech-eng.com"
                  className="text-blue-700 hover:text-blue-800 underline"
                >
                  towertech-eng.com
                </a>{' '}
                and provides engineering and technical support services for
                industrial projects in China. This Privacy Policy explains what
                information we collect through this website, how it is used and
                how it is protected.
              </p>

              <Section id="information-collected" title="1. Information We Collect">
                <p>
                  We collect information you voluntarily submit through the
                  project inquiry / contact form on this website, which may
                  include:
                </p>
                <ul className="list-disc pl-6 space-y-1.5">
                  <li>Name</li>
                  <li>Company</li>
                  <li>Email address</li>
                  <li>Phone number or WhatsApp contact (if provided)</li>
                  <li>Project location</li>
                  <li>Expected project start date</li>
                  <li>Type of engineering or technical support required</li>
                  <li>Project details and any additional information you provide in the inquiry</li>
                  <li>Any other information you voluntarily submit in written correspondence</li>
                </ul>
                <p>
                  If you apply for a position or send us your CV through the
                  Careers section of this website, we additionally collect the
                  information you voluntarily provide in the application,
                  including your name, contact details (email and phone /
                  WhatsApp / WeChat), current location, CV / resume file and
                  any message you submit. This recruitment information is
                  processed for the purposes described in section 2 below.
                </p>
                <p>
                  In addition, our web server may automatically log basic
                  technical information such as IP address, browser type and
                  approximate access times for the purpose of website security
                  and technical operation.
                </p>
              </Section>

              <Section id="use-of-information" title="2. How We Use Information">
                <p>The information we collect is used to:</p>
                <ul className="list-disc pl-6 space-y-1.5">
                  <li>Respond to project inquiries submitted through the website</li>
                  <li>Evaluate project requirements and personnel needs</li>
                  <li>Communicate with potential customers about project support</li>
                  <li>Prepare quotations, proposals or project discussions</li>
                  <li>Provide requested engineering and technical services</li>
                  <li>
                    Review applications and CVs submitted through the Careers
                    section for recruitment and project staffing purposes,
                    including evaluating candidate suitability, contacting
                    applicants regarding open or future positions, and
                    coordinating personnel for industrial projects
                  </li>
                  <li>Maintain website security and technical operation</li>
                </ul>
                <p>
                  We do not sell your personal data and we do not use the
                  information collected through this website for advertising
                  purposes.
                </p>
              </Section>

              <Section id="data-retention" title="3. Data Retention">
                <p>
                  Project inquiry information is retained only for as long as
                  necessary to respond to the inquiry, evaluate a potential
                  project, and fulfil any resulting business relationship, or
                  as required by applicable law.
                </p>
                <p>
                  If no business relationship results from an inquiry, the
                  submitted information will be deleted or anonymized within a
                  reasonable period.
                </p>
              </Section>

              <Section id="data-security" title="4. Data Security">
                <p>
                  We apply reasonable technical and organizational measures to
                  protect submitted information against unauthorized access,
                  disclosure or loss. Email transmission and internet
                  communications, however, are not fully secure and we cannot
                  guarantee absolute security of any information transmitted to
                  us over the internet.
                </p>
              </Section>

              <Section id="third-parties" title="5. Third-Party Service Providers">
                <p>
                  We may use third-party service providers to operate the
                  website and to deliver email communications (for example,
                  hosting infrastructure and SMTP email delivery providers).
                  These providers process information strictly on our behalf and
                  only to the extent necessary to perform the requested service.
                </p>
                <p>
                  We do not share your personal information with third parties
                  for marketing purposes.
                </p>
              </Section>

              <Section id="cookies" title="6. Cookies and Browser Storage">
                <p>
                  This website uses only technically necessary cookies and
                  browser storage required for the site to function. We do not
                  use analytics cookies or advertising cookies. Further
                  information is available in our{' '}
                  <Link
                    to="/cookie-policy"
                    className="text-blue-700 hover:text-blue-800 underline"
                  >
                    Cookie Policy
                  </Link>
                  .
                </p>
              </Section>

              <Section id="user-rights" title="7. Your Rights">
                <p>
                  Subject to applicable law, you may request access to,
                  correction of, or deletion of the personal information you
                  have submitted through this website. To exercise these
                  rights, please contact us using the details below.
                </p>
              </Section>

              <Section id="international" title="8. International Data Processing">
                <p>
                  Tower Technology is based in China. Information submitted
                  through this website may be processed and stored in China or
                  by service providers operating in other jurisdictions.
                  Wherever the information is processed, we apply the
                  protections described in this Privacy Policy.
                </p>
              </Section>

              <Section id="updates" title="9. Updates to This Policy">
                <p>
                  We may update this Privacy Policy from time to time to
                  reflect changes in our practices or applicable law. Updated
                  versions will be posted on this page with a revised effective
                  date.
                </p>
              </Section>

              {/* Contact */}
              <div className="bg-gradient-to-br from-blue-700 to-blue-900 rounded-xl p-6 md:p-8 text-white mt-8">
                <div className="flex items-start space-x-4">
                  <div className="inline-flex items-center justify-center w-11 h-11 bg-white/20 rounded-lg flex-shrink-0">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-xl md:text-2xl font-bold mb-2">
                      10. Contact
                    </h2>
                    <p className="text-blue-100 mb-4 leading-relaxed">
                      For any questions about this Privacy Policy or to
                      exercise your rights, please contact:
                    </p>
                    <a
                      href="mailto:office@towertech-eng.com"
                      className="inline-flex items-center text-white font-semibold hover:text-blue-100 transition-colors break-all"
                    >
                      <Mail className="w-4 h-4 mr-2 flex-shrink-0" />
                      office@towertech-eng.com
                    </a>
                    <div className="mt-4 text-sm text-blue-100 leading-relaxed">
                      <div className="font-semibold text-white">
                        Xiamen Tower Technology Co., Ltd.
                      </div>
                      <div>Xiamen (Fujian), China</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-10">
              <Link
                to="/"
                data-testid="privacy-back-home"
                className="inline-flex items-center px-6 py-3 bg-blue-700 hover:bg-blue-800 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};
